import axios from "../../../../api/axios";

/**
 * Category API Endpoints
 */
export const CATEGORY_ENDPOINTS = {
    BASE: 'v2/inventory/categories',
    BY_ID: (id) => `v2/inventory/categories/${id}`,
};

/**
 * Centralized error handler for category API
 */
const handleCategoryError = (error, defaultMessage) => {
    const message = error.response?.data?.message || error.message || defaultMessage;
    throw new Error(message);
};

/**
 * Get all categories
 * @returns {Promise<Array>} List of inventory item categories
 */
export const getAllCategories = async () => {
    try {
        const response = await axios.get(CATEGORY_ENDPOINTS.BASE);
        return response.data?.categories || [];
    } catch (error) {
        handleCategoryError(error, 'Failed to fetch categories');
    }
};

/**
 * Get category by ID
 * @param {string} id - Category ID
 * @returns {Promise<Object>} Category object
 */
export const getCategoryById = async (id) => {
    try {
        const response = await axios.get(CATEGORY_ENDPOINTS.BY_ID(id));
        return response.data?.data;
    } catch (error) {
        handleCategoryError(error, 'Failed to fetch category');
    }
};

/**
 * Create a new category
 * @param {Object} payload - Category data (name)
 * @returns {Promise<Object>} Created category
 */
export const createCategory = async (payload) => {
    try {
        const response = await axios.post(CATEGORY_ENDPOINTS.BASE, payload);
        return response.data?.data;
    } catch (error) {
        handleCategoryError(error, 'Failed to create category');
    }
};

/**
 * Update an existing category
 * @param {string} id - Category ID
 * @param {Object} payload - Updated data (name)
 * @returns {Promise<Object>} Updated category
 */
export const updateCategory = async (id, payload) => {
    try {
        const response = await axios.put(CATEGORY_ENDPOINTS.BY_ID(id), payload);
        return response.data?.data;
    } catch (error) {
        handleCategoryError(error, 'Failed to update category');
    }
};

/**
 * Delete a category by ID
 * @param {string} id - Category ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteCategory = async (id) => {
    try {
        const response = await axios.delete(CATEGORY_ENDPOINTS.BY_ID(id));
        return response.data;
    } catch (error) {
        handleCategoryError(error, 'Failed to delete category');
    }
};
